import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Ban, Check, CheckSquare, ChevronDown, ChevronRight, CirclePause, Square, Trash2 } from "lucide-react";
import { toast } from "@/lib/toast";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { MonitorInboxItem } from "./MonitorInboxItem";
import type { PaperWithRole } from "@/types/paper";
import type { FacetSummary } from "@/types/concept";

interface Props {
  papers: PaperWithRole[];
  conceptId: string;
  onTriage: (paperId: string, action: "keep" | "dismiss" | "restore") => void;
  onTriageBatch: (paperIds: string[], action: "keep" | "dismiss") => void;
  onDismissWithReason?: (paperId: string, reason: string, reasonText?: string, findSimilar?: boolean) => void;
  autoDismissThreshold: number | null;
  lastMonitorRun?: string | null;
  nextMonitorRun?: string | null;
  /** Scheduled runs skip while untriaged >= this (owner's plan); null = unknown. */
  backlogLimit?: number | null;
  monitorEnabled?: boolean;
  onInbox?: (paperId: string, mode: "copy" | "move") => void;
  /** False while an overlay (the full-page PDF viewer) owns the keyboard. */
  keyboardEnabled?: boolean;
  /** Facets 1c-2: active angles (pass only at 2+) for the best-angle badge. */
  angles?: FacetSummary[];
}

function effectiveScore(p: PaperWithRole): number {
  return p.user_score_override ?? p.normalized_score;
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "---";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const THRESHOLD_OPTIONS = [20, 30, 40, 50, 60] as const;

function DismissBelowPicker({
  papers,
  onDismiss,
}: {
  papers: PaperWithRole[];
  onDismiss: (paperIds: string[], action: "dismiss") => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm">
          <Trash2 className="mr-1 size-3.5" />
          Dismiss below score...
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-44 p-1">
        {THRESHOLD_OPTIONS.map((threshold) => {
          const count = papers.filter(
            (p) => effectiveScore(p) < threshold,
          ).length;
          return (
            <button
              key={threshold}
              type="button"
              disabled={count === 0}
              onClick={() => {
                const ids = papers
                  .filter((p) => effectiveScore(p) < threshold)
                  .map((p) => p.id);
                if (ids.length > 0) {
                  onDismiss(ids, "dismiss");
                  toast.success(`Dismissed ${ids.length} paper${ids.length !== 1 ? "s" : ""} below score ${threshold}`);
                }
                setOpen(false);
              }}
              className="flex w-full items-center justify-between rounded px-2 py-1.5 text-sm hover:bg-accent disabled:opacity-40 disabled:hover:bg-transparent"
            >
              <span>Below {threshold}</span>
              <span className="text-xs text-muted-foreground">{count}</span>
            </button>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}

export function MonitorInbox({
  papers,
  conceptId,
  onTriage,
  onTriageBatch,
  onDismissWithReason,
  autoDismissThreshold,
  lastMonitorRun,
  nextMonitorRun,
  backlogLimit = null,
  monitorEnabled = false,
  onInbox,
  keyboardEnabled = true,
  angles,
}: Props) {
  const [focusIndex, setFocusIndex] = useState(0);
  const [showAutoDismissed, setShowAutoDismissed] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const lastClickedIndexRef = useRef<number | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Split papers
  const untriaged = useMemo(() => {
    return papers
      .filter(
        (p) =>
          (p.added_source === "monitor" || p.added_source === "trawl_references") &&
          !p.triaged &&
          !p.exclusion_reason,
      )
      .sort((a, b) => effectiveScore(b) - effectiveScore(a));
  }, [papers]);

  const autoDismissed = useMemo(() => {
    return papers
      .filter((p) => p.exclusion_reason === "auto:below_threshold")
      .sort((a, b) => effectiveScore(b) - effectiveScore(a));
  }, [papers]);

  // Clamp focus index
  useEffect(() => {
    if (focusIndex >= untriaged.length) {
      setFocusIndex(Math.max(0, untriaged.length - 1));
    }
  }, [focusIndex, untriaged.length]);

  // Scroll focused item into view
  useEffect(() => {
    const container = listRef.current;
    if (!container) return;
    const items = container.querySelectorAll("[data-testid='inbox-item']");
    const item = items[focusIndex];
    if (item && typeof item.scrollIntoView === "function") {
      item.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [focusIndex]);

  // --- Selection helpers ---

  const selectRange = useCallback(
    (from: number, to: number) => {
      const lo = Math.min(from, to);
      const hi = Math.max(from, to);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        for (let i = lo; i <= hi; i++) {
          const p = untriaged[i];
          if (p) next.add(p.id);
        }
        return next;
      });
    },
    [untriaged],
  );

  const toggleSelect = useCallback(
    (paperId: string, shiftKey?: boolean) => {
      const idx = untriaged.findIndex((p) => p.id === paperId);
      if (shiftKey && lastClickedIndexRef.current != null && idx >= 0) {
        // Shift+checkbox: range select
        selectRange(lastClickedIndexRef.current, idx);
        lastClickedIndexRef.current = idx;
        return;
      }
      if (idx >= 0) lastClickedIndexRef.current = idx;
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(paperId)) next.delete(paperId);
        else next.add(paperId);
        return next;
      });
    },
    [untriaged, selectRange],
  );

  const selectAll = useCallback(() => {
    setSelectedIds(new Set(untriaged.map((p) => p.id)));
  }, [untriaged]);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  // Clean up selection when papers leave the untriaged list
  useEffect(() => {
    const untriagedIds = new Set(untriaged.map((p) => p.id));
    setSelectedIds((prev) => {
      const next = new Set([...prev].filter((id) => untriagedIds.has(id)));
      if (next.size === prev.size) return prev;
      return next;
    });
  }, [untriaged]);

  const handleSelectionAction = useCallback(
    (action: "keep" | "dismiss") => {
      const ids = [...selectedIds];
      if (ids.length > 0) {
        onTriageBatch(ids, action);
        const label = action === "keep" ? "Kept" : "Dismissed";
        toast.success(`${label} ${ids.length} paper${ids.length !== 1 ? "s" : ""}`);
        setSelectedIds(new Set());
      }
    },
    [selectedIds, onTriageBatch],
  );

  // Complement action for the keeper-first flow: dismiss every unselected
  // paper. The selection survives (selected papers stay untriaged), so
  // "select keepers → Dismiss others → Keep" completes triage in two clicks.
  const handleDismissOthers = useCallback(() => {
    const ids = untriaged.filter((p) => !selectedIds.has(p.id)).map((p) => p.id);
    if (ids.length > 0) {
      onTriageBatch(ids, "dismiss");
      toast.success(`Dismissed ${ids.length} unselected paper${ids.length !== 1 ? "s" : ""}`);
    }
  }, [untriaged, selectedIds, onTriageBatch]);

  // Row click: shift = range, cmd/ctrl = toggle, plain = single-select
  const handleRowClick = useCallback(
    (index: number, e: React.MouseEvent) => {
      const paperId = untriaged[index]?.id;
      if (!paperId) return;

      if (e.shiftKey && lastClickedIndexRef.current != null) {
        // Shift-click: select range from last clicked to current
        selectRange(lastClickedIndexRef.current, index);
      } else if (e.metaKey || e.ctrlKey) {
        // Cmd/Ctrl-click: toggle single item
        toggleSelect(paperId);
      } else {
        // Plain click: single-select (replace selection)
        setSelectedIds(new Set([paperId]));
      }
      lastClickedIndexRef.current = index;
    },
    [untriaged, selectRange, toggleSelect],
  );

  // --- Keyboard navigation ---

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!keyboardEnabled) return;
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setFocusIndex((i) => {
            const next = Math.min(i + 1, untriaged.length - 1);
            if (e.shiftKey && untriaged[next]) {
              setSelectedIds((prev) => new Set([...prev, untriaged[next].id]));
            }
            return next;
          });
          break;
        case "ArrowUp":
          e.preventDefault();
          setFocusIndex((i) => {
            const next = Math.max(i - 1, 0);
            if (e.shiftKey && untriaged[next]) {
              setSelectedIds((prev) => new Set([...prev, untriaged[next].id]));
            }
            return next;
          });
          break;
        case "Enter": {
          e.preventDefault();
          const paper = untriaged[focusIndex];
          if (paper) {
            onTriage(paper.id, "keep");
            toast.success(`Kept: ${paper.title.slice(0, 60)}${paper.title.length > 60 ? "..." : ""}`);
          }
          break;
        }
        case "d": {
          e.preventDefault();
          const paper = untriaged[focusIndex];
          if (paper) {
            onTriage(paper.id, "dismiss");
            toast.success(`Dismissed: ${paper.title.slice(0, 60)}${paper.title.length > 60 ? "..." : ""}`);
          }
          break;
        }
        case " ": {
          e.preventDefault();
          const paper = untriaged[focusIndex];
          if (paper) toggleSelect(paper.id);
          break;
        }
      }
    },
    [untriaged, focusIndex, onTriage, toggleSelect, keyboardEnabled],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  // Batch handlers
  const handleKeepAll = useCallback(() => {
    const ids = untriaged.map((p) => p.id);
    if (ids.length > 0) {
      onTriageBatch(ids, "keep");
      toast.success(`Kept all ${ids.length} remaining papers`);
    }
  }, [untriaged, onTriageBatch]);


  const allSelected = selectedIds.size > 0 && selectedIds.size >= untriaged.length;

  return (
    <div className="flex h-full flex-col" data-guide-target="researchInboxSection">
      {/* Toolbar */}
      <div className="flex items-center gap-2 border-b px-3 py-2">
        <span className="text-sm font-medium text-foreground">
          {untriaged.length} paper{untriaged.length !== 1 ? "s" : ""} to review
        </span>
        <div className="ml-auto flex items-center gap-2">
          {untriaged.length > 0 && (
            <Button variant="outline" size="sm" onClick={handleKeepAll}>
              <Check className="mr-1 size-3.5" />
              Keep all remaining
            </Button>
          )}
          <DismissBelowPicker papers={untriaged} onDismiss={onTriageBatch} />
        </div>
      </div>

      {/* Backlog pause — mirrors the backend's live-count rule, so it clears
          the moment triage drops below the limit (spec 2026-09-28). */}
      {monitorEnabled && backlogLimit != null && untriaged.length >= backlogLimit && (
        <div
          className="flex items-start gap-1.5 border-b border-amber-500/20 bg-amber-500/5 px-3 py-1.5 text-xs text-amber-400"
          data-testid="monitor-backlog-paused"
        >
          <CirclePause className="mt-px size-3.5 shrink-0" />
          <span>
            Monitor paused — {untriaged.length} papers waiting. Scheduled runs resume once fewer
            than {backlogLimit} are left to review.
          </span>
        </div>
      )}

      {/* Selection bar — matches existing SelectionBar styling */}
      {selectedIds.size > 0 && (
        <div
          className="flex items-center gap-2 border-b px-3 py-1.5"
          style={{ background: "rgba(137,180,250,0.08)", borderColor: "rgba(137,180,250,0.2)" }}
        >
          <span className="text-xs font-semibold" style={{ color: "#89b4fa" }}>
            {selectedIds.size} selected
          </span>
          <div className="mx-1 h-4 w-px" style={{ background: "rgba(137,180,250,0.2)" }} />

          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            onClick={() => handleSelectionAction("keep")}
          >
            <Check className="mr-1 size-3.5" />
            Keep
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            onClick={() => handleSelectionAction("dismiss")}
          >
            <Ban className="mr-1 size-3.5" />
            Dismiss
          </Button>
          {selectedIds.size < untriaged.length && (
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              onClick={handleDismissOthers}
              title="Dismiss every unselected paper; the selection stays for you to keep"
            >
              <Ban className="mr-1 size-3.5" />
              Dismiss others ({untriaged.length - selectedIds.size})
            </Button>
          )}

          <div className="mx-1 h-4 w-px" style={{ background: "rgba(137,180,250,0.2)" }} />

          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs"
            disabled={allSelected}
            onClick={selectAll}
          >
            {allSelected ? (
              <CheckSquare className="mr-1 size-3.5" />
            ) : (
              <Square className="mr-1 size-3.5" />
            )}
            All
          </Button>
          <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={clearSelection}>
            <Square className="mr-1 size-3.5" />
            None
          </Button>
        </div>
      )}

      {/* Content */}
      <div ref={listRef} className="flex-1 overflow-y-auto">
        {/* Completion state */}
        {untriaged.length === 0 && (
          <div className="flex flex-col items-center justify-center gap-2 py-12 text-center" data-testid="inbox-complete">
            <div className="text-3xl">&#127881;</div>
            <h3 className="text-lg font-semibold text-foreground">
              You&apos;re caught up!
            </h3>
            <div className="space-y-0.5 text-xs text-muted-foreground">
              {lastMonitorRun && (
                <p>Last run: {formatDate(lastMonitorRun)}</p>
              )}
              {nextMonitorRun && (
                <p>Next run: {formatDate(nextMonitorRun)}</p>
              )}
            </div>
          </div>
        )}

        {/* Untriaged list */}
        {untriaged.length > 0 && (
          <div className="space-y-1 p-2">
            {untriaged.map((paper, idx) => (
              <MonitorInboxItem
                key={paper.id}
                paper={paper}
                conceptId={conceptId}
                angles={angles}
                onTriage={onTriage}
                onDismissWithReason={onDismissWithReason}
                isFocused={idx === focusIndex}
                isSelected={selectedIds.has(paper.id)}
                onToggleSelect={toggleSelect}
                onRowClick={(e) => handleRowClick(idx, e)}
                onFocus={() => setFocusIndex(idx)}
                onInbox={onInbox}
              />
            ))}
          </div>
        )}

        {/* Auto-dismissed section */}
        {autoDismissed.length > 0 && (
          <div className="border-t">
            <button
              type="button"
              onClick={() => setShowAutoDismissed(!showAutoDismissed)}
              className="flex w-full items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:bg-muted/30"
            >
              {showAutoDismissed ? (
                <ChevronDown className="size-4" />
              ) : (
                <ChevronRight className="size-4" />
              )}
              <span>
                {autoDismissed.length} auto-dismissed paper{autoDismissed.length !== 1 ? "s" : ""}
              </span>
              {autoDismissThreshold != null && (
                <span className="text-xs">(below score {autoDismissThreshold})</span>
              )}
            </button>

            {showAutoDismissed && (
              <div className="space-y-1 px-2 pb-2">
                {autoDismissed.map((paper) => (
                  <MonitorInboxItem
                    key={paper.id}
                    paper={paper}
                    conceptId={conceptId}
                    angles={angles}
                    onTriage={onTriage}
                    isAutoDismissed
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Keyboard hint footer */}
      <div className="border-t px-3 py-1.5 text-center text-[11px] text-muted-foreground">
        ↑/↓ navigate &middot; Shift+↑/↓ extend selection &middot; Space select &middot; Enter keep &middot; d dismiss &middot; Shift+click range
      </div>
    </div>
  );
}
